import type { ReactNode } from 'react';

import { Text } from '@zillow/constellation-10';

export const SIGN_ONLINE = {
  SHARE_LEASE_CONTENT: {
    DESCRIPTION_TEXT: (
      <>
        We’ll email the below people a link to view and sign this lease. They’ll need to log in or create a new account
        to do so. You’ll be able to track who has signed from your leases dashboard.
      </>
    ),
    DEFAULT_PERSONAL_NOTE:
      `Here’s the lease for you to look over. Let me know if you have any questions or ` +
      `concerns. If everything looks good, please sign before the lease’s start date or we’ll ` +
      `have to start the signing again. I’ll sign after everyone else has and I’ve received the deposit. Thanks!`,
    FAQ: [
      {
        label: 'What happens next?',
        description: (
          <Text>
            Your recipients will get an email with a link to view and sign this lease.
            <br />
            <br />
            If changes are needed, first cancel signing. This cancels any signatures and allows you to edit the lease.
            Once your changes are made, request signatures again.
          </Text>
        ),
      },
      {
        label: 'How do I download a copy of this lease?',
        description: (
          <Text>
            You can download a complete draft of your lease from your leases dashboard or from a confirmation screen
            after you request signatures. The file will be suitable to review and print, but it is not meant to be
            signed.
          </Text>
        ),
      },
      {
        label: 'What if my renters and I want to sign a printed version of this lease?',
        description: (
          <Text>
            Choose the <strong>Print to sign</strong> option above. Once your renters review the lease, you can print
            out signable copies and arrange a signing.
          </Text>
        ),
      },
      {
        label: 'How else can I make sure this lease is valid?',
        description: (
          <Text>
            First off, do not hand-write changes on a printed copy of the lease. If you need to make changes, do that
            here in Zillow Rental Manager then re-share the lease with your tenants.
            <br />
            <br />
            We also recommend not countersigning your final lease until you have received any requested security
            deposits.
          </Text>
        ),
      },
    ],
    PRIMARY_BUTTON_LABEL: 'Send request for signatures',
  },
  LEASE_SENT_CONTENT: {
    TITLE: 'Signatures requested',
    FAQ: [
      {
        label: 'What happens next?',
        description: (
          <Text>
            Your recipients will get an email with a link to view and sign this lease.
            <br />
            <br />
            If changes are needed, first cancel signing. This cancels any signatures and allows you to edit the lease.
            Once your changes are made, request signatures again.
          </Text>
        ),
      },
      {
        label: 'How do I download a copy of this lease?',
        description: (
          <Text>
            You can download a complete draft of your lease from your leases dashboard or from a confirmation screen
            after you request signatures. The file will be suitable to review and print, but it is not meant to be
            signed.
          </Text>
        ),
      },
      {
        label: 'What if my renters and I want to sign a printed version of this lease?',
        description: (
          <Text>
            First cancel signing of the lease, then choose the <strong>Print to sign</strong> option when requesting
            signatures. Once your renters review the lease, you can print out signable copies and arrange a signing.
          </Text>
        ),
      },
      {
        label: 'How else can I make sure this lease is valid?',
        description: (
          <Text>
            First off, do not hand-write changes on a printed copy of the lease. If you need to make changes, do that
            here in Zillow Rental Manager then re-share the lease with your tenants.
            <br />
            <br />
            We also recommend not countersigning your final lease until you have received any requested security
            deposits.
          </Text>
        ),
      },
    ],
    PRIMARY_BUTTON_LABEL: 'Download draft PDF',
    LEASE_DRAFT: true,
  },
};

export const PRINT_TO_SIGN = {
  SHARE_LEASE_CONTENT: {
    DESCRIPTION_TEXT: (
      <>
        We’ll email the below people a link to an un-signable copy of the lease. They’ll need to log in or create a new
        account to view it. Once everyone has reviewed the lease, print them each a final copy and arrange the signing.
      </>
    ),
    DEFAULT_PERSONAL_NOTE:
      `Here’s the lease for you to look over. Let me know if you have any questions or concerns. ` +
      `If everything looks good, I’ll arrange for us to walk through the lease, sign it, ` +
      `and take care of the deposit. Thanks!`,
    FAQ: [
      {
        label: 'What happens next?',
        description: (
          <Text>
            Your recipients will get an email with a link to view an un-signable copy of this lease. You are the only
            one who can download a signable copy of the lease.
            <br />
            <br />
            If changes are needed, make them here in Zillow Rental Manager then request signatures again.
            <br />
            <br />
            If no more changes are needed, download and print signable copies of the lease and arrange a signing.
          </Text>
        ),
      },
      {
        label: 'How do I download a signable copy of this lease?',
        description: (
          <Text>
            After you send your lease for review, you’ll be able to download a signable copy from a confirmation screen
            or from your leases dashboard. You are the only party who can download a signable copy of the lease.
          </Text>
        ),
      },
      {
        label: 'How can my tenants and I sign this lease electronically?',
        description: (
          <Text>
            Start by choosing the <strong>Sign online</strong> option above. From there, you and your tenants will be
            able to adopt electronic signatures and sign online.
          </Text>
        ),
      },
      {
        label: 'How else can I make sure this lease is valid?',
        description: (
          <Text>
            First off, do not handwrite changes on a printed copy of the lease. If you need to make changes, do that
            here in Zillow Rental Manager then re-send the lease.
            <br />
            <br />
            We also recommend not countersigning your final lease until you have received any requested security
            deposits.
          </Text>
        ),
      },
    ],
    PRIMARY_BUTTON_LABEL: 'Send lease for review',
  },
  LEASE_SENT_CONTENT: {
    TITLE: 'Message sent',
    FAQ: [
      {
        label: 'What happens next?',
        description: (
          <Text>
            Your recipients will get an email with a link to view an un-signable copy of this lease. You are the only
            one who can download a signable copy of the lease.
            <br />
            <br />
            If changes are needed, make them here in Zillow Rental Manager then send the lease for review again.
            <br />
            <br />
            If no more changes are needed, download and print signable copies of the lease and arrange a signing.
          </Text>
        ),
      },
      {
        label: 'How do I download a signable copy of this lease?',
        description: (
          <Text>
            You can download a copy of your lease from your leases dashboard or by clicking{' '}
            <strong>Download signable PDF</strong> below.
          </Text>
        ),
      },
      {
        label: 'Will my tenants be able to sign this lease electronically?',
        description: (
          <Text>
            No. You are currently the only party who can access a signable copy of this lease, and it is meant to be
            printed out. If you and your renters would like to sign electronically, first cancel this signing from your
            leases dashboard, then request signatures again. From there, choose the <strong>Sign online</strong> option.
          </Text>
        ),
      },
      {
        label: 'How else can I make sure this lease is valid?',
        description: (
          <Text>
            First off, do not handwrite changes on a printed copy of the lease. If you need to make changes, do that
            here in Zillow Rental Manager then re-send the lease.
            <br />
            <br />
            We also recommend not countersigning your final lease until you have received any requested security
            deposits.
          </Text>
        ),
      },
    ],
    PRIMARY_BUTTON_LABEL: 'Download signable PDF',
    LEASE_DRAFT: false,
  },
};

export const SELF_UPLOAD = {
  SHARE_LEASE_CONTENT: {
    DESCRIPTION_TEXT: (
      <>
        We’ll email the below people a link to view and sign this lease. They‘ll need to log in or create a new account
        to do so.
      </>
    ),
    DEFAULT_PERSONAL_NOTE:
      `Here’s the lease for you to look over. If everything looks good, please sign before the ` +
      `lease’s start date. I’ll sign after everyone else. Thanks!`,
    FAQ: [
      {
        label: 'What happens next?',
        description: (
          <Text>
            Your recipients will get an email with a link to view and sign this lease.
            <br />
            <br />
            If changes are needed, first cancel signing. This cancels any signatures and allows you to edit the lease.
            Once your changes are made, request signatures again.
          </Text>
        ),
      },
    ],
    PRIMARY_BUTTON_LABEL: 'Request electronic signatures',
  },
  LEASE_SENT_CONTENT: {
    TITLE: 'Signatures requested',
    FAQ: [
      {
        label: 'What happens next?',
        description: (
          <Text>
            Now you can track who’s signed from your leases dashboard.
            <br />
            <br />
            Note that you will need to request signatures again if everyone does not sign the lease before the date it’s
            scheduled to go into effect.
          </Text>
        ),
      },
      {
        label: 'What if I need to change the lease before everyone signs?',
        description: (
          <Text>
            If changes are needed, first cancel signing. This cancels any signatures and allows you to edit the lease.
            Once your changes are made, reupload your lease and request signatures again.
          </Text>
        ),
      },
    ],
    PRIMARY_BUTTON_LABEL: null,
    LEASE_DRAFT: false,
  },
};

export function getZillowLeasesDisclaimer(stateFullName: string): string {
  return `
      By using this lease, I understand and agree that while Zillow has drafted this lease to comply with ${stateFullName || 'my region'}’s legal requirements, there may be specific circumstances that require additional
      revisions in order to comply with local law and the parties to this lease may have rights or obligations under law
      that are not addressed in this lease or the other materials provided on our platform. Zillow is not responsible
      for any subsequent changes to this lease, including whether those changes comply with applicable law. Zillow is
      not responsible for the legality of any “custom rules” I include in this lease. Zillow is providing access to
      self-help services and is not acting as my attorney. Zillow cannot provide any legal advice regarding my use of
      this lease.
    `;
}

export const SELF_UPLOAD_SIGN_DISCLAIMER =
  'By using this feature to sign my lease, I understand and agree that Zillow has taken no part in the creation of my lease and that Zillow makes no representation that my lease complies with any applicable local legal requirements. I understand that I am signing my lease as a single document and my tenant will receive any required disclosures or addenda as part of my lease. I understand that Zillow is providing access to self-help services and not acting as my attorney or providing any legal advice regarding my lease. If you have any questions about the legal rights, obligations, or implications of this lease, please consult with an attorney.';

export const SELF_UPLOAD_UPLOAD_DISCLAIMER: ReactNode = (
  <>
    By uploading my lease, I understand and agree that Zillow has taken no part in the creation of my lease and that
    Zillow makes no representation that my lease complies with any applicable local legal requirements. I understand
    that I am uploading my lease as a single document and my tenant will receive any required disclosures or addenda as
    part of my lease. I understand that Zillow is providing access to self-help services and not acting as my attorney
    or providing any legal advice regarding my lease.
  </>
);
